import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Spin, Tooltip, message } from 'antd';
import {
  CloseOutlined,
  LoadingOutlined,
  MessageOutlined,
  PlusOutlined,
  RobotOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthSession } from '../../../app/providers/AuthSessionProvider';
import { copilotKeys } from '../../../shared/api/cacheKeys';
import { hasPermission } from '../../../shared/auth/permissions';
import { ENABLE_COPILOT } from '../../../shared/utils/constants';
import { getFlowMeta } from '../../../shared/ui/layout/flowMeta';
import {
  approveCopilotRun,
  createCopilotMessage,
  createCopilotThread,
  fetchCopilotConfig,
  fetchCopilotRun,
  fetchCopilotThread,
  fetchCopilotThreads,
} from '../api/queries';
import { useCopilotPageContext } from '../context/CopilotPageContext';
import styles from './WorkspaceCopilot.module.css';

const QUICK_PROMPTS = [
  'Explain this page for me.',
  'Which invoices are overdue right now?',
  'Explain why cash is low this month.',
  'Draft a follow-up email for this invoice.',
];

function normalizeText(value, maxLength = 180) {
  return String(value || '').trim().slice(0, maxLength);
}

function buildPageMeta(pathname = '') {
  const flowMeta = getFlowMeta(pathname);

  return {
    section: normalizeText(flowMeta.section, 80),
    title: normalizeText(flowMeta.title, 120),
    summary: normalizeText(flowMeta.summary, 240),
    tips: Array.isArray(flowMeta.tips)
      ? flowMeta.tips
          .map((tip) => normalizeText(tip, 180))
          .filter(Boolean)
          .slice(0, 4)
      : [],
    quickActions: Array.isArray(flowMeta.quickActions)
      ? flowMeta.quickActions
          .map((action) => ({
            label: normalizeText(action?.label, 80),
            path: normalizeText(action?.path, 240),
          }))
          .filter((action) => action.label && action.path)
          .slice(0, 4)
      : [],
  };
}

function normalizeEntityContext(entityContext) {
  if (!entityContext || typeof entityContext !== 'object') {
    return null;
  }

  const nextContext = {
    entityType: normalizeText(entityContext.entityType, 60),
    entityId: normalizeText(entityContext.entityId, 120),
    label: normalizeText(entityContext.label, 180),
    customerId: normalizeText(entityContext.customerId, 120),
    invoiceId: normalizeText(entityContext.invoiceId, 120),
    invoiceNumber: normalizeText(entityContext.invoiceNumber, 120),
    customerName: normalizeText(entityContext.customerName, 180),
    status: normalizeText(entityContext.status, 80),
    email: normalizeText(entityContext.email, 180),
    documentType: normalizeText(entityContext.documentType, 40),
  };

  return Object.values(nextContext).some(Boolean) ? nextContext : null;
}

export function buildRouteContext(pathname = '', entityContext = null) {
  const segments = String(pathname || '')
    .split('/')
    .filter(Boolean);
  const entityId =
    segments[1] === 'view' || segments[1] === 'edit'
      ? segments[2] || ''
      : '';
  const normalizedEntityContext = normalizeEntityContext(entityContext);

  return {
    path: pathname,
    featureKey: segments[0] || 'workspace',
    entityId,
    pageMeta: buildPageMeta(pathname),
    ...(normalizedEntityContext ? { entityContext: normalizedEntityContext } : {}),
  };
}

function formatMessageTime(value) {
  if (!value) {
    return '';
  }

  try {
    return new Date(value).toLocaleTimeString('en-IN', {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (_error) {
    return '';
  }
}

function buildOptimisticMessage(content = '') {
  return {
    id: 'pending-user-message',
    role: 'user',
    content,
    createdAt: new Date().toISOString(),
    pending: true,
    metadata: {},
  };
}

function getMessageSecurityNotice(messageItem) {
  return String(messageItem?.metadata?.security?.notice || '').trim();
}

function getMessageActions(messageItem) {
  return Array.isArray(messageItem?.metadata?.actions) ? messageItem.metadata.actions : [];
}

function getDraftPreview(messageItem) {
  return messageItem?.metadata?.draftPreview && typeof messageItem.metadata.draftPreview === 'object'
    ? messageItem.metadata.draftPreview
    : null;
}

function resolveAssistantState({ configured, loading, thinking }) {
  if (loading) {
    return {
      label: 'Connecting',
      tone: 'loading',
    };
  }

  if (!configured) {
    return {
      label: 'Needs setup',
      tone: 'muted',
    };
  }

  if (thinking) {
    return {
      label: 'Reviewing your billing data',
      tone: 'active',
    };
  }

  return {
    label: 'Ready to help',
    tone: 'ready',
  };
}

export default function WorkspaceCopilot() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scrollViewportRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const { session } = useAuthSession();
  const { entityContext } = useCopilotPageContext();
  const [open, setOpen] = useState(false);
  const [composerValue, setComposerValue] = useState('');
  const [selectedThreadId, setSelectedThreadId] = useState('');
  const [selectedRunId, setSelectedRunId] = useState('');
  const [pendingPreviewMessage, setPendingPreviewMessage] = useState('');
  const [draftFreshThread, setDraftFreshThread] = useState(false);
  const permissions = session?.permissions || [];
  const canUseCopilot =
    ENABLE_COPILOT &&
    Boolean(session?.accessToken) &&
    hasPermission(permissions, 'copilot:use');
  const routeContext = useMemo(
    () => buildRouteContext(location.pathname, entityContext),
    [entityContext, location.pathname]
  );

  const copilotConfigQuery = useQuery({
    queryKey: copilotKeys.config(),
    queryFn: fetchCopilotConfig,
    enabled: canUseCopilot && open,
  });

  const threadsQuery = useQuery({
    queryKey: copilotKeys.threads(),
    queryFn: fetchCopilotThreads,
    enabled: canUseCopilot && open,
  });

  useEffect(() => {
    if (
      !open ||
      draftFreshThread ||
      selectedThreadId ||
      !Array.isArray(threadsQuery.data) ||
      !threadsQuery.data.length
    ) {
      return;
    }

    setSelectedThreadId(threadsQuery.data[0].id);
  }, [draftFreshThread, open, selectedThreadId, threadsQuery.data]);

  const selectedThreadQuery = useQuery({
    queryKey: copilotKeys.thread(selectedThreadId),
    queryFn: () => fetchCopilotThread(selectedThreadId),
    enabled: canUseCopilot && open && Boolean(selectedThreadId),
  });

  useEffect(() => {
    if (!selectedThreadId) {
      setSelectedRunId('');
      return;
    }

    const latestRunId =
      selectedThreadQuery.data?.thread?.lastRunId ||
      selectedThreadQuery.data?.runs?.[0]?.id ||
      '';

    setSelectedRunId(latestRunId);
  }, [selectedThreadId, selectedThreadQuery.data]);

  const runQuery = useQuery({
    queryKey: copilotKeys.run(selectedRunId),
    queryFn: () => fetchCopilotRun(selectedRunId),
    enabled: canUseCopilot && open && Boolean(selectedRunId),
    refetchInterval: (query) => (query.state.data?.run?.status === 'running' ? 1500 : false),
  });

  const createMessageMutation = useMutation({
    mutationFn: async ({ content }) => {
      let threadId = selectedThreadId;

      if (!threadId) {
        const createdThread = await createCopilotThread({
          title: content.slice(0, 80),
          routeContext,
        });
        threadId = createdThread.id;
      }

      const response = await createCopilotMessage(threadId, {
        content,
        routeContext,
      });

      return {
        threadId,
        response,
      };
    },
    onSuccess: ({ threadId, response }) => {
      setDraftFreshThread(false);
      setPendingPreviewMessage('');
      setSelectedThreadId(threadId);
      if (response?.latestRun?.id) {
        setSelectedRunId(response.latestRun.id);
        queryClient.setQueryData(copilotKeys.run(response.latestRun.id), {
          run: response.latestRun,
          toolCalls: response.latestToolCalls || [],
        });
      }
      queryClient.setQueryData(copilotKeys.thread(threadId), {
        thread: response.thread,
        messages: response.messages || [],
        runs: response.runs || [],
      });
      queryClient.invalidateQueries({ queryKey: copilotKeys.threads() });
      setComposerValue('');
    },
    onError: (error) => {
      setPendingPreviewMessage('');
      message.error(error?.message || 'Copilot could not finish this request.');
    },
  });

  const approveRunMutation = useMutation({
    mutationFn: ({ runId, approved }) => approveCopilotRun(runId, { approved }),
    onSuccess: (response, variables) => {
      if (response?.run?.id) {
        setSelectedRunId(response.run.id);
        queryClient.setQueryData(copilotKeys.run(response.run.id), response);
      }
      queryClient.invalidateQueries({ queryKey: copilotKeys.thread(selectedThreadId) });
      queryClient.invalidateQueries({ queryKey: copilotKeys.threads() });
      const feedback = variables?.approved
        ? 'Copilot is finishing that follow-up.'
        : 'Copilot cancelled the send request.';
      message.success(feedback);
    },
    onError: (error) => {
      message.error(error?.message || 'Copilot could not finish that action.');
    },
  });

  const activeRun = runQuery.data?.run || null;
  const threadMessages = selectedThreadQuery.data?.messages || [];
  const hasOptimisticDuplicate =
    pendingPreviewMessage &&
    threadMessages.some(
      (messageItem) =>
        messageItem.role === 'user' &&
        String(messageItem.content || '').trim() === pendingPreviewMessage
    );
  const visibleMessages =
    pendingPreviewMessage && !hasOptimisticDuplicate
      ? [...threadMessages, buildOptimisticMessage(pendingPreviewMessage)]
      : threadMessages;
  const isThinking = createMessageMutation.isPending || activeRun?.status === 'running';
  const assistantState = resolveAssistantState({
    configured: Boolean(copilotConfigQuery.data?.configured),
    loading: copilotConfigQuery.isLoading,
    thinking: isThinking,
  });

  const submitPrompt = (value) => {
    const content = String(value || '').trim();
    if (!content || createMessageMutation.isPending || !copilotConfigQuery.data?.configured) {
      return;
    }

    setPendingPreviewMessage(content);
    createMessageMutation.mutate({ content });
  };

  const startFreshChat = () => {
    setDraftFreshThread(true);
    setSelectedThreadId('');
    setSelectedRunId('');
    setComposerValue('');
    setPendingPreviewMessage('');
  };

  const handleAction = (action = {}, fallbackRunId = '') => {
    if (!action || typeof action !== 'object') {
      return;
    }

    if (action.kind === 'navigate' && action.path) {
      navigate(action.path);
      return;
    }

    if (action.kind === 'prompt' && action.promptText) {
      submitPrompt(action.promptText);
      return;
    }

    if (action.kind === 'approve' || action.kind === 'reject') {
      const runId = String(action.runId || fallbackRunId || '').trim();
      if (!runId || approveRunMutation.isPending) {
        return;
      }

      approveRunMutation.mutate({
        runId,
        approved: action.kind === 'approve',
      });
    }
  };

  useEffect(() => {
    if (!open) {
      shouldStickToBottomRef.current = true;
      return undefined;
    }

    const viewport = scrollViewportRef.current;
    if (!viewport) {
      return undefined;
    }

    const handleScroll = () => {
      const remainingDistance =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      shouldStickToBottomRef.current = remainingDistance <= 72;
    };

    handleScroll();
    viewport.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      viewport.removeEventListener('scroll', handleScroll);
    };
  }, [open, selectedThreadId]);

  useEffect(() => {
    if (!open) {
      setDraftFreshThread(false);
      return;
    }

    const viewport = scrollViewportRef.current;
    if (!viewport) {
      return;
    }

    const hasPendingActivity = Boolean(pendingPreviewMessage) || isThinking;
    if (!hasPendingActivity && !shouldStickToBottomRef.current) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      viewport.scrollTop = viewport.scrollHeight;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isThinking, open, pendingPreviewMessage, selectedThreadId, visibleMessages.length]);

  if (!canUseCopilot) {
    return null;
  }

  return (
    <>
      <Tooltip title="Billing Copilot" placement="left">
        <button
          type="button"
          className={styles.launcher}
          onClick={() => setOpen((currentValue) => !currentValue)}
          aria-label={open ? 'Close billing copilot' : 'Open billing copilot'}
          data-testid="workspace-copilot-launcher"
        >
          <span className={styles.launcherPulse} />
          <span className={styles.launcherIcon}>
            <MessageOutlined />
          </span>
        </button>
      </Tooltip>

      <AnimatePresence>
        {open ? (
          <motion.section
            className={styles.panel}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <header className={styles.header}>
              <div className={styles.headerGlow} />
              <div className={styles.headerPrimary}>
                <div className={styles.avatar}>
                  <RobotOutlined />
                </div>
                <div className={styles.headerCopy}>
                  <div className={styles.headerTitleRow}>
                    <strong>Billing Copilot</strong>
                    <span
                      className={`${styles.statusPill} ${
                        assistantState.tone === 'active'
                          ? styles.statusActive
                          : assistantState.tone === 'ready'
                            ? styles.statusReady
                            : assistantState.tone === 'loading'
                              ? styles.statusLoading
                              : styles.statusMuted
                      }`}
                    >
                      {assistantState.label}
                    </span>
                  </div>
                  <p className={styles.headerSubtitle}>
                    Ask about invoices, collections, cash flow, and customer activity in plain
                    language.
                  </p>
                </div>
              </div>

              <div className={styles.headerActions}>
                <Button
                  type="text"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={startFreshChat}
                >
                  New chat
                </Button>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  aria-label="Close billing copilot"
                  onClick={() => {
                    setDraftFreshThread(false);
                    setOpen(false);
                  }}
                />
              </div>
            </header>

            <div className={styles.chatSurface}>
              {!copilotConfigQuery.isLoading && !copilotConfigQuery.data?.configured ? (
                <div className={styles.setupNotice}>
                  Copilot needs AI setup from your admin before it can reply here.
                </div>
              ) : null}

              <div
                className={styles.messageViewport}
                ref={scrollViewportRef}
                data-testid="workspace-copilot-message-viewport"
              >
                {copilotConfigQuery.isLoading ? (
                  <div className={styles.centerState}>
                    <Spin size="small" />
                    <span>Preparing your copilot workspace...</span>
                  </div>
                ) : selectedThreadQuery.isLoading ? (
                  <div className={styles.centerState}>
                    <Spin size="small" />
                    <span>Loading your conversation...</span>
                  </div>
                ) : visibleMessages.length ? (
                  <div className={styles.messageList}>
                    {visibleMessages.map((messageItem) => {
                      const isUser = messageItem.role === 'user';
                      const messageActions = isUser ? [] : getMessageActions(messageItem);
                      const draftPreview = isUser ? null : getDraftPreview(messageItem);

                      return (
                        <div
                          key={messageItem.id}
                          className={`${styles.messageRow} ${
                            isUser ? styles.messageRowUser : styles.messageRowAssistant
                          }`}
                        >
                          {!isUser ? (
                            <div className={styles.messageAvatar}>
                              <RobotOutlined />
                            </div>
                          ) : null}

                          <div
                            className={`${styles.messageGroup} ${
                              isUser ? styles.messageGroupUser : styles.messageGroupAssistant
                            }`}
                          >
                            <div
                              className={`${styles.messageBubble} ${
                                isUser ? styles.messageBubbleUser : styles.messageBubbleAssistant
                              }`}
                            >
                              <p className={styles.messageContent}>{messageItem.content}</p>
                            </div>
                            {draftPreview ? (
                              <div className={styles.draftPreview}>
                                <div className={styles.draftPreviewHeader}>
                                  <strong>
                                    {draftPreview.documentLabel || 'Email draft'}
                                  </strong>
                                  <span
                                    className={`${styles.draftStatus} ${
                                      draftPreview.sendReadiness === 'ready'
                                        ? styles.draftStatusReady
                                        : styles.draftStatusBlocked
                                    }`}
                                  >
                                    {draftPreview.sendReadiness === 'ready'
                                      ? 'Ready to send'
                                      : 'Needs attention'}
                                  </span>
                                </div>
                                <dl className={styles.draftMeta}>
                                  <div>
                                    <dt>To</dt>
                                    <dd>{draftPreview.recipient || 'Recipient needed'}</dd>
                                  </div>
                                  <div>
                                    <dt>Subject</dt>
                                    <dd>{draftPreview.subject || 'No subject'}</dd>
                                  </div>
                                </dl>
                                {draftPreview.note ? (
                                  <p className={styles.draftNote}>{draftPreview.note}</p>
                                ) : null}
                                <p className={styles.draftBody}>{draftPreview.message || ''}</p>
                              </div>
                            ) : null}
                            {messageActions.length ? (
                              <div className={styles.actionRow}>
                                {messageActions.map((action, index) => {
                                  const runId = String(action.runId || messageItem.runId || '');
                                  const isApprovalAction =
                                    action.kind === 'approve' || action.kind === 'reject';
                                  const actionKey = `${messageItem.id}:${action.kind}:${
                                    action.path || action.promptText || action.label || index
                                  }`;

                                  return (
                                    <button
                                      key={actionKey}
                                      type="button"
                                      className={`${styles.actionChip} ${
                                        action.kind === 'approve'
                                          ? styles.actionChipApprove
                                          : action.kind === 'reject'
                                            ? styles.actionChipReject
                                            : styles.actionChipNeutral
                                      }`}
                                      onClick={() => handleAction(action, runId)}
                                      disabled={
                                        createMessageMutation.isPending ||
                                        (isApprovalAction && approveRunMutation.isPending)
                                      }
                                    >
                                      {action.label}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : null}
                            {getMessageSecurityNotice(messageItem) ? (
                              <div className={styles.securityNotice}>
                                {getMessageSecurityNotice(messageItem)}
                              </div>
                            ) : null}
                            <span className={styles.messageTime}>
                              {messageItem.pending ? 'Sending...' : formatMessageTime(messageItem.createdAt)}
                            </span>
                          </div>
                        </div>
                      );
                    })}

                    {isThinking ? (
                      <div className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
                        <div className={styles.messageAvatar}>
                          <RobotOutlined />
                        </div>
                        <div className={styles.messageGroup}>
                          <div className={`${styles.messageBubble} ${styles.messageBubbleAssistant}`}>
                            <div className={styles.typingRow}>
                              <LoadingOutlined />
                              <span>Reviewing your billing workspace...</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className={styles.welcomeState}>
                    <div className={styles.welcomeBadge}>Always-on billing assistant</div>
                    <h3 className={styles.welcomeTitle}>
                      Start a conversation, not a workflow.
                    </h3>
                    <p className={styles.welcomeBody}>
                      Billing Copilot is designed to answer questions quickly, explain what changed,
                      and help you understand collections without exposing internal system steps.
                    </p>

                    <div className={styles.promptGrid}>
                      {QUICK_PROMPTS.map((prompt) => (
                        <button
                          key={prompt}
                          type="button"
                          className={styles.promptChip}
                          onClick={() => submitPrompt(prompt)}
                          disabled={!copilotConfigQuery.data?.configured || createMessageMutation.isPending}
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <footer className={styles.composerWrap} data-testid="workspace-copilot-composer">
                <div className={styles.composerShell}>
                  <Input.TextArea
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    value={composerValue}
                    onChange={(event) => setComposerValue(event.target.value)}
                    onPressEnter={(event) => {
                      if (event.shiftKey) {
                        return;
                      }

                      event.preventDefault();
                      submitPrompt(composerValue);
                    }}
                    placeholder={
                      copilotConfigQuery.data?.configured
                        ? 'Ask a billing question...'
                        : 'Copilot is waiting for admin setup'
                    }
                    className={styles.composerInput}
                    disabled={!copilotConfigQuery.data?.configured}
                  />

                  <button
                    type="button"
                    className={styles.sendButton}
                    onClick={() => submitPrompt(composerValue)}
                    disabled={
                      !composerValue.trim() ||
                      !copilotConfigQuery.data?.configured ||
                      createMessageMutation.isPending
                    }
                    aria-label="Send message to billing copilot"
                  >
                    {createMessageMutation.isPending ? <LoadingOutlined /> : <SendOutlined />}
                  </button>
                </div>

                <div className={styles.footerMeta}>
                  <span>
                    {Array.isArray(threadsQuery.data) && threadsQuery.data.length
                      ? `Continuing ${threadsQuery.data.length} saved copilot chats in this workspace.`
                      : 'Responses are grounded in your billing workspace data.'}
                  </span>
                  <span>
                    {approveRunMutation.isPending
                      ? 'Finishing your approved action...'
                      : 'Press Enter to send, Shift + Enter for a new line.'}
                  </span>
                </div>
              </footer>
            </div>
          </motion.section>
        ) : null}
      </AnimatePresence>
    </>
  );
}
